import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Market Regime card — pure observability. Polls the
 * `/api/dashboard/market-regime` endpoint every `pollIntervalMs`
 * (default 45s) and renders the current EMA+ADX regime label plus
 * diagnostics. Renders even when the active profile's gate is
 * DISABLED (mode = "monitoring_only") so the operator can watch the
 * classifier before flipping the switch.
 *
 * No UI polish. No styling fluff. Operator-grade panel.
 */
export function MarketRegimeCard({ symbol = "BTCUSDT", pollIntervalMs = 45000 }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const tick = async () => {
      try {
        const d = await api.dashboardMarketRegime(symbol);
        if (mounted.current) {
          setSnap(d);
          setErr(null);
        }
      } catch (e) {
        if (mounted.current) setErr(e?.response?.data?.detail || e.message || "fetch failed");
      }
    };
    tick();
    const id = setInterval(tick, pollIntervalMs);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [symbol, pollIntervalMs]);

  if (err) {
    return (
      <div
        data-testid="dashboard-market-regime-card-error"
        className="bg-[#0F0F14] border border-[#FF3B30]/30 rounded-sm p-3 text-xs font-mono text-[#FF3B30]"
      >
        Market regime feed unavailable: {String(err).slice(0, 200)}
      </div>
    );
  }
  if (!snap) {
    return (
      <div
        data-testid="dashboard-market-regime-card-loading"
        className="bg-[#0F0F14] border border-border rounded-sm p-3 text-xs font-mono text-[#A0A0AB]"
      >
        Market Regime · loading…
      </div>
    );
  }

  // Classifier now returns FINAL 3-state labels only: UPTREND /
  // DOWNTREND / CHOP. The legacy STRONG_*/NEUTRAL keys remain in the
  // colour map for graceful rendering if any cached response still
  // carries them — but production code paths emit only the 3-state set.
  const labelColor = {
    UPTREND:          "text-[#10B981]",
    DOWNTREND:        "text-[#FF3B30]",
    CHOP:             "text-[#FBBF24]",
    // legacy / defensive fallbacks
    STRONG_UPTREND:   "text-[#10B981]",
    NEUTRAL:          "text-[#A0A0AB]",
    STRONG_DOWNTREND: "text-[#FF3B30]",
  }[snap.label] || "text-[#E4E4E7]";

  // Helper labels for the fallback_reason field — operator-readable.
  const reasonLabel = {
    neutral_resolved_chop:      "raw NEUTRAL → CHOP (low confidence)",
    neutral_resolved_bullish:   "raw NEUTRAL → UPTREND (score-resolved)",
    neutral_resolved_bearish:   "raw NEUTRAL → DOWNTREND (score-resolved)",
    neutral_tie_to_chop:        "raw NEUTRAL → CHOP (tied scores)",
    warmup_fallback_to_chop:    "warmup → CHOP",
    fallback_to_chop:           "fail-open → CHOP",
    index_out_of_range_fallback_to_chop: "no data → CHOP",
  }[snap.fallback_reason] || null;

  const modeBadge =
    snap.gate_mode === "active_gate"
      ? <span className="text-[10px] bg-[#10B981]/20 text-[#10B981] px-1 py-0.5 rounded">ACTIVE GATE</span>
      : <span className="text-[10px] bg-[#A0A0AB]/15 text-[#A0A0AB] px-1 py-0.5 rounded">monitoring only</span>;

  const fmtTs = (ms) => {
    if (!ms) return "—";
    try { return new Date(ms).toISOString().replace("T", " ").slice(0, 19) + " UTC"; }
    catch { return String(ms); }
  };
  const fmtAge = (ms) => {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60_000)}m`;
  };

  return (
    <div
      data-testid="dashboard-market-regime-card"
      className="bg-[#0F0F14] border border-border rounded-sm p-4 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-[#A0A0AB]">
          Market Regime
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-[#71717A]">
            {snap.symbol} · {snap.regime_timeframe}
          </span>
          {modeBadge}
        </div>
      </div>

      <div className="flex items-baseline gap-3">
        <span
          data-testid="dashboard-market-regime-label"
          className={`text-2xl font-mono font-semibold ${labelColor}`}
        >
          {snap.label}
        </span>
        {snap.raw_label && snap.raw_label !== snap.label ? (
          <span
            data-testid="dashboard-market-regime-raw-label"
            className="text-[10px] font-mono text-[#71717A]"
            title="raw 6-state label before collapse"
          >
            raw=<span className="text-[#A0A0AB]">{snap.raw_label}</span>
          </span>
        ) : null}
        <span className="text-[10px] font-mono text-[#71717A]">
          src={snap.source}{snap.cache_age_ms != null ? ` · age=${fmtAge(snap.cache_age_ms)}` : ""}
          {snap.stale_for_ms ? ` · stale=${fmtAge(snap.stale_for_ms)}` : ""}
        </span>
      </div>

      {reasonLabel ? (
        <div
          data-testid="dashboard-market-regime-fallback-reason"
          className="text-[10px] font-mono text-[#FBBF24]"
        >
          {reasonLabel}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] font-mono">
        <div className="text-[#71717A]">ADX</div>
        <div data-testid="dashboard-market-regime-adx" className="text-right">
          {Number(snap.adx ?? 0).toFixed(2)} (+{Number(snap.plus_di ?? 0).toFixed(1)} / -{Number(snap.minus_di ?? 0).toFixed(1)})
        </div>

        <div className="text-[#71717A]">EMA fast / slow</div>
        <div className="text-right">
          {Number(snap.ema_fast ?? 0).toFixed(2)} {snap.ema_relation === "fast>slow" ? "▲" : snap.ema_relation === "fast<slow" ? "▼" : "≈"} {Number(snap.ema_slow ?? 0).toFixed(2)}
        </div>

        <div className="text-[#71717A]">EMA slope</div>
        <div className="text-right">
          {snap.ema_slope_direction === "up" ? "↑ up" : snap.ema_slope_direction === "down" ? "↓ down" : "→ flat"}
          <span className="text-[#71717A] ml-1">({Number(snap.ema_slope_value ?? 0).toFixed(2)})</span>
        </div>

        <div className="text-[#71717A]">Last closed candle</div>
        <div className="text-right">{fmtTs(snap.last_closed_candle_ts_ms)}</div>

        <div className="text-[#71717A]">Scores (bull / bear / chop)</div>
        <div
          data-testid="dashboard-market-regime-scores"
          className="text-right"
        >
          <span className="text-[#10B981]">{Number(snap.bullish_score ?? 0).toFixed(2)}</span>
          {" / "}
          <span className="text-[#FF3B30]">{Number(snap.bearish_score ?? 0).toFixed(2)}</span>
          {" / "}
          <span className="text-[#FBBF24]">{Number(snap.chop_score ?? 0).toFixed(2)}</span>
          <span className="text-[#71717A] ml-1">(conf={Number(snap.confidence ?? 0).toFixed(2)})</span>
        </div>

        <div className="text-[#71717A]">Allows LONG entry</div>
        <div
          data-testid="dashboard-market-regime-allow-long"
          className={`text-right ${snap.effective_allow_long_entry ? "text-[#10B981]" : "text-[#FF3B30]"}`}
        >
          {snap.effective_allow_long_entry ? "YES" : "NO"}
          {snap.gate_enabled_for_active_profile && !snap.regime_allows_long ? " (blocked by regime)" : ""}
          {!snap.profile_allow_long ? " (blocked by profile)" : ""}
        </div>

        <div className="text-[#71717A]">Allows SHORT entry</div>
        <div
          data-testid="dashboard-market-regime-allow-short"
          className={`text-right ${snap.effective_allow_short_entry ? "text-[#10B981]" : "text-[#FF3B30]"}`}
        >
          {snap.effective_allow_short_entry ? "YES" : "NO"}
          {snap.gate_enabled_for_active_profile && !snap.regime_allows_short ? " (blocked by regime)" : ""}
          {!snap.profile_allow_short ? " (blocked by profile)" : ""}
        </div>

        {snap.active_profile_name ? (
          <>
            <div className="text-[#71717A]">Active profile</div>
            <div className="text-right text-[#E4E4E7] truncate">{snap.active_profile_name}</div>
          </>
        ) : null}
      </div>

      {/* ----------------------------------------------------------------
          CHOP Quality Size Scaling — LOCKED V2 CORE BEHAVIOR
          ----------------------------------------------------------------
          Display-only badge. Not configurable. Shows the current tier
          (HIGH / MEDIUM / LOW) and the multiplier that will be applied
          to the next CHOP_CVD entry if the regime stays CHOP.
       */}
      {snap.chop_quality_size ? (
        <div
          data-testid="dashboard-chop-quality-size"
          className="mt-2 pt-2 border-t border-border/40 grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] font-mono"
        >
          <div className="text-[#71717A] flex items-center gap-2">
            CHOP quality sizing
            <span
              className="text-[9px] bg-[#A0A0AB]/10 text-[#A0A0AB] px-1 py-0.5 rounded uppercase tracking-wider"
              title="Part of locked V2 core behavior — not configurable"
            >
              locked v2
            </span>
          </div>
          <div className="text-right">
            {snap.chop_quality_size.engaged ? (
              <span
                data-testid="dashboard-chop-quality-tier-badge"
                className={
                  snap.chop_quality_size.tier === "HIGH"
                    ? "text-[#10B981]"
                    : snap.chop_quality_size.tier === "MEDIUM"
                    ? "text-[#FBBF24]"
                    : "text-[#FF3B30]"
                }
              >
                {snap.chop_quality_size.tier} ·{" "}
                <span className="text-[#E4E4E7]">
                  ×{Number(snap.chop_quality_size.multiplier).toFixed(2)}
                </span>
              </span>
            ) : (
              <span className="text-[#71717A]">— (non-CHOP)</span>
            )}
          </div>

          <div className="text-[#71717A]">ATR% (2 h)</div>
          <div data-testid="dashboard-chop-quality-atr-pct" className="text-right">
            {Number(snap.chop_quality_size.atr_pct ?? 0).toFixed(2)}%
          </div>

          <div className="text-[#71717A]">Tier thresholds</div>
          <div className="text-right text-[#A0A0AB]">
            HIGH &lt; {Number(snap.chop_quality_size.thresholds_pct?.high_max ?? 0.75).toFixed(2)}% ·
            MED &lt; {Number(snap.chop_quality_size.thresholds_pct?.med_max ?? 1.50).toFixed(2)}% ·
            LOW ≥ {Number(snap.chop_quality_size.thresholds_pct?.med_max ?? 1.50).toFixed(2)}%
          </div>

          <div className="text-[#71717A]">Tier multipliers</div>
          <div className="text-right text-[#A0A0AB]">
            ×{Number(snap.chop_quality_size.tier_multipliers?.HIGH ?? 0.5).toFixed(2)} ·
            ×{Number(snap.chop_quality_size.tier_multipliers?.MEDIUM ?? 0.25).toFixed(2)} ·
            ×{Number(snap.chop_quality_size.tier_multipliers?.LOW ?? 0.1).toFixed(2)}
          </div>
        </div>
      ) : null}

      {snap.reason ? (
        <div className="text-[10px] font-mono text-[#FBBF24]">{snap.reason}</div>
      ) : null}
    </div>
  );
}

export default MarketRegimeCard;
